import React, { useEffect, useState } from 'react';
import { Query } from 'react-apollo';
import PropTypes from 'prop-types';
import classnames from 'classnames';

import { BasePage, MobileScreen, TabletScreen } from '@sm/webassets';
import {
  Typography,
  Card,
  Grid,
  Col,
  Row,
  Button,
  Container
} from '@sm/wds-react';

import './solutions.scss';
import modulesQuery from './solutions.graphql';
import Carousel from './components/Carousel';
import { johnsonJohnson, box, gopro, lyft, progressive } from './static/images';

const solutionImage = img => {
  if (img && img.indexOf('https://') > -1) {
    return img;
  }
  return 'https://www.petguide.com/wp-content/uploads/2017/09/medium-sized-dog-breeds-whippet.jpg';
};

const solutionCard = solution => (
  <Card className="sm-solutions__solution wds-m-b-5" padding={false}>
    <img
      src={solutionImage(solution.image)}
      alt={solution.title}
      className="sm-solutions__solution-image"
    />
    <div className="wds-flex__item--grow">
      <Typography variant="cardTitle">{solution.title}</Typography>
      <Typography>{solution.tagline}</Typography>
    </div>
    <Container mt={5}>
      <Typography variant="bodySm" component="span">
        Starting at&nbsp;
      </Typography>
      <Typography component="span" className="sm-solutions__solution-price">
        {new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: 'USD'
        }).format(solution.price.basePrice)}
      </Typography>
    </Container>
    <Grid className="wds-m-t-4" noBleeds>
      <Row>
        <Col xl={6} lg={12} md={6} xs={12}>
          <Button
            href={`/solutions/create/${solution.id}`}
            stretched
            color="upgrade"
            className="wds-m-b-3"
          >
            Get started
          </Button>
        </Col>
        <Col xl={6} lg={12} md={6} xs={12}>
          <Button
            variant="ghost"
            color="secondary"
            stretched
            className="wds-m-b-3"
            href={
              solution.links.marketingPage !== 'not_yet_used'
                ? solution.links.marketingPage
                : null
            }
          >
            Learn more
          </Button>
        </Col>
      </Row>
    </Grid>
  </Card>
);

const AnimatedSolution = ({ delay, children }) => {
  const [startAnimation, setStartAnimation] = useState(false);
  useEffect(() => {
    const timeout = setTimeout(() => {
      setStartAnimation(true);
    }, delay);
    return () => {
      clearTimeout(timeout);
    };
  }, [delay]);

  const classList = classnames('sm-solutions__solution-animated-container', {
    'sm-solutions__solution-animated-container--started': startAnimation
  });

  return <div className={classList}>{children}</div>;
};
AnimatedSolution.propTypes = {
  delay: PropTypes.number.isRequired,
  children: PropTypes.node.isRequired
};

const FooterLogo = ({ alt, src }) => (
  <img alt={alt} src={src} className="sm-solutions__footer-logo" />
);
FooterLogo.propTypes = {
  alt: PropTypes.string.isRequired,
  src: PropTypes.string.isRequired
};

const Solutions = () => {
  return (
    <BasePage
      color="transparent"
      includeHeader
      includeFooter={false}
      pageId="Market Research - Solutions"
      legacyWeb=""
    >
      <div className="sm-solutions">
        <Container
          className="sm-solutions__hero"
          pr={5}
          pl={5}
          flex
          flexDirection="column"
          flexJustify="center"
          flexAlign="center"
        >
          <div className="sm-solutions__hero-text-wrapper">
            <Typography
              color="light"
              variant="cardTitle"
              className="sm-solutions__hero-name"
            >
              SURVEYMONKEY EXPERT SOLUTIONS
            </Typography>
            <Typography
              color="light"
              variant="hero1"
              component="h1"
              className="sm-solutions__hero-tagline wds-m-t-3"
            >
              <strong>Automate your market research</strong>
            </Typography>
            <Typography
              color="light"
              variant="cardTitle"
              className="sm-solutions__hero-description wds-m-t-3"
            >
              Get polished, presentation-ready insights—in as little as an hour!
              Our solutions help build your survey based on expert methodology,
              target your audience, and analyze your data for you.
            </Typography>
          </div>
        </Container>
        <Query query={modulesQuery}>
          {({ loading, error, data }) => (
            <Choose>
              <When condition={loading}>Loading</When>
              <When condition={error}>We failed miserably</When>
              <Otherwise>
                <>
                  <div>
                    <MobileScreen>
                      <Carousel className="sm-solutions__solution-container--mobile">
                        {data.modules.items

                          .filter(s => !!s.title && !!s.tagline) // TODO: remove when we have better data
                          .map(solution => (
                            <div key={solution.id}>
                              {solutionCard(solution)}
                            </div>
                          ))}
                      </Carousel>
                    </MobileScreen>
                    <TabletScreen>
                      <Grid className="sm-solutions__solution-container">
                        <Row>
                          {data.modules.items
                            .filter(s => !!s.title && !!s.tagline) // TODO: remove when we have better data
                            .map((solution, index) => (
                              <Col
                                xs={12}
                                sm={12}
                                md={6}
                                lg={4}
                                key={solution.id}
                              >
                                <AnimatedSolution
                                  delay={2000 + (index + 1) * 200}
                                >
                                  {solutionCard(solution)}
                                </AnimatedSolution>
                              </Col>
                            ))}
                        </Row>
                      </Grid>
                    </TabletScreen>
                  </div>
                  <Container className="sm-solutions__footer" px={4} py={7}>
                    <Typography variant="sectionTitle">
                      SurveyMonkey is used in 335+ organization worldwide
                    </Typography>
                    <Grid className="wds-m-t-4">
                      <Row className="sm-solutions__footer-logos-row">
                        <Col sm={4} lg={2}>
                          <FooterLogo
                            alt="Johnson & Johnson"
                            src={johnsonJohnson}
                          />
                        </Col>
                        <Col sm={4} lg={2}>
                          <FooterLogo alt="box" src={box} />
                        </Col>
                        <Col sm={4} lg={2}>
                          <FooterLogo alt="GoPro" src={gopro} />
                        </Col>
                        <Col sm={6} lg={2}>
                          <FooterLogo alt="lyft" src={lyft} />
                        </Col>
                        <Col sm={6} lg={2}>
                          <FooterLogo alt="Progressive" src={progressive} />
                        </Col>
                      </Row>
                    </Grid>
                  </Container>
                </>
              </Otherwise>
            </Choose>
          )}
        </Query>
      </div>
    </BasePage>
  );
};

export default Solutions;
